import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Pencil, Plus } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Alternador, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { CabecalhoConfig } from './config-crm-base';
import { rotuloStatusProposta, useStatusProposta, type Proposta, type StatusProposta } from './FichaPropostas';

/**
 * Lote crm6 (ficha 2026-10-29, item 18, decisão do Gustavo): CRM > Configurações do CRM > Status de proposta, no padrão das
 * outras seções dali (texto de abertura, "Criar", lista em cartão com as ações na linha e o formulário no drawer). Cada status
 * tem nome, cor (um dos tokens do selo), estado base, ordem (subir e descer) e ativo. Desativar status em uso é recusado pelo
 * servidor com a explicação (como a etapa com oportunidades), que aparece no aviso; a linha mostra quantas propostas usam cada um.
 */

const CLASSE_LINHA = 'flex flex-wrap items-center gap-3 border-t border-borda px-4 py-3 text-sm first:border-t-0';
const CORES: CorSelo[] = ['neutro', 'acento', 'roxo', 'azul', 'ciano', 'laranja', 'dourado', 'verde', 'perigo'];
const ESTADOS: Proposta['estado'][] = ['criada', 'enviada', 'aceita', 'recusada'];

export function StatusPropostaCrm() {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useStatusProposta();
  const [editando, setEditando] = useState<StatusProposta | 'novo' | null>(null);
  // Val5: os status de proposta são configuração do CRM (`pipeline.configurar`).
  const podeEditar = s.pode('pipeline.configurar');
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline', 'proposta-status'] });
  const acao = useMutation({ mutationFn: (f: () => Promise<unknown>) => f(), onSuccess: recarregar, onError: (e) => toast.erro(mensagemDeErro(e)) });
  const mover = (lista: StatusProposta[], i: number, d: -1 | 1) => {
    const ids = lista.map((x) => x.id);
    [ids[i], ids[i + d]] = [ids[i + d]!, ids[i]!];
    acao.mutate(() => post('/pipeline/proposta-status/reordenar', { ids }));
  };
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-status-proposta="">
      {/* Lote crm7: o cabeçalho das seções novas da origem, com o "Criar status" à direita. */}
      <CabecalhoConfig
        titulo={t('crm.configuracoes.secoes.status-proposta')}
        intro={<p>{t('crm.configuracoes.statusProposta.intro')}</p>}
        acao={
          podeEditar && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setEditando('novo')} data-criar-status-proposta="">
              {t('crm.configuracoes.statusProposta.criar')}
            </Botao>
          )
        }
      />
      <Estado consulta={q}>
        {(d) => (
          <ul className="rounded-xl border border-borda bg-sup-1">
            {d.itens.map((x, i) => {
              const nome = rotuloStatusProposta(t, x.nome, x.estado_base);
              const travado = x.ativo && x.em_uso > 0;
              return (
                <li key={x.id} className={CLASSE_LINHA} data-status-config={x.nome}>
                  {/* Rodada rlote-4 (VIS-B1): o nome e o estado base empilhados num bloco que encolhe; o nome longo (até 60) quebra a
                      390 px em vez de deixar o estado base com 2 px e a página rolar na horizontal. */}
                  <span className="flex min-w-0 flex-1 basis-48 flex-col items-start gap-1" data-status-nome="">
                    <Selo cor={x.cor} quebra>
                      {nome}
                    </Selo>
                    <span className="text-xs text-tinta-3">
                      {t('crm.configuracoes.statusProposta.estadoBase')}: {t(`crm.proposta.estados.${x.estado_base}`)}
                    </span>
                  </span>
                  {x.fabrica && <Selo>{t('crm.configuracoes.statusProposta.fabrica')}</Selo>}
                  {!x.ativo && <Selo cor="laranja">{t('crm.configuracoes.statusProposta.inativo')}</Selo>}
                  <span className="numero text-xs text-tinta-3" title={travado ? t('crm.configuracoes.statusProposta.emUsoAviso') : undefined} data-em-uso={x.em_uso}>
                    {t('crm.configuracoes.statusProposta.emUso', { count: x.em_uso })}
                  </span>
                  {podeEditar && (
                    <span className="flex items-center gap-1">
                      <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.configuracoes.statusProposta.subir', { nome })} icone={<ArrowUp className="size-4" />} disabled={i === 0 || acao.isPending} onClick={() => mover(d.itens, i, -1)} data-subir-status="" />
                      <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.configuracoes.statusProposta.descer', { nome })} icone={<ArrowDown className="size-4" />} disabled={i === d.itens.length - 1 || acao.isPending} onClick={() => mover(d.itens, i, 1)} data-descer-status="" />
                      <Alternador
                        marcado={x.ativo}
                        rotulo={t(x.ativo ? 'crm.configuracoes.statusProposta.desativar' : 'crm.configuracoes.statusProposta.ativar', { nome })}
                        aoMudar={(v) => acao.mutate(() => patch(`/pipeline/proposta-status/${x.id}`, { ativo: v }))}
                      />
                      <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.configuracoes.statusProposta.editarNome', { nome })} icone={<Pencil className="size-4" />} onClick={() => setEditando(x)} data-editar-status="" />
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Estado>
      {editando && <FormStatus atual={editando === 'novo' ? null : editando} aoFechar={() => setEditando(null)} aoSalvar={recarregar} />}
    </section>
  );
}

function FormStatus({ atual, aoFechar, aoSalvar }: { atual: StatusProposta | null; aoFechar: () => void; aoSalvar: () => Promise<unknown> }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const id = useId();
  const [nome, setNome] = useState(atual ? rotuloStatusProposta(t, atual.nome, atual.estado_base) : '');
  const [cor, setCor] = useState<CorSelo>(atual?.cor ?? 'neutro');
  const [estadoBase, setEstadoBase] = useState<Proposta['estado']>(atual?.estado_base ?? 'enviada');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const { ref } = useValidacaoLocal(erro ? { nome: erro } : null);
  const baseTravada = !!atual && (atual.fabrica || atual.em_uso > 0);
  const salvar = async () => {
    if (!nome.trim()) return setErro(t('crm.form.obrigatorio'));
    setSalvando(true);
    try {
      // O nome de fábrica intacto (traduzido na tela) não é regravado no idioma de quem edita.
      const nomeFinal = atual && nome.trim() === rotuloStatusProposta(t, atual.nome, atual.estado_base) ? atual.nome : nome.trim();
      if (atual) await patch(`/pipeline/proposta-status/${atual.id}`, { nome: nomeFinal, cor, ...(baseTravada ? {} : { estado_base: estadoBase }) });
      else await post('/pipeline/proposta-status', { id: crypto.randomUUID(), nome: nomeFinal, cor, estado_base: estadoBase });
      toast.sucesso(t('crm.configuracoes.salvo'));
      await aoSalvar();
      aoFechar();
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={atual ? t('crm.configuracoes.statusProposta.editar') : t('crm.configuracoes.statusProposta.criar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvando} onClick={() => void salvar()} data-salvar-status-proposta="">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <form ref={ref} noValidate className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()} data-form-status-proposta="">
        <Rotulo id={`${id}-nome`} rotulo={t('crm.configuracoes.statusProposta.nome')} obrigatorio erro={erro ?? undefined}>
          <Entrada id={`${id}-nome`} data-nome-status="" maxLength={60} value={nome} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <Rotulo id={`${id}-cor`} rotulo={t('crm.configuracoes.statusProposta.cor')}>
          <Selecao id={`${id}-cor`} data-cor-status="" value={cor} onChange={(e) => setCor(e.target.value as CorSelo)} opcoes={CORES.map((c) => ({ valor: c, rotulo: t(`crm.configuracoes.statusProposta.cores.${c}`) }))} />
        </Rotulo>
        <div>
          <Selo cor={cor}>{nome.trim() || t('crm.configuracoes.statusProposta.nome')}</Selo>
        </div>
        <Rotulo id={`${id}-base`} rotulo={t('crm.configuracoes.statusProposta.estadoBase')} ajuda={baseTravada ? t('crm.configuracoes.statusProposta.estadoBaseTravado') : t('crm.configuracoes.statusProposta.estadoBaseAjuda')}>
          <Selecao id={`${id}-base`} data-base-status="" value={estadoBase} disabled={baseTravada} onChange={(e) => setEstadoBase(e.target.value as Proposta['estado'])} opcoes={ESTADOS.map((x) => ({ valor: x, rotulo: t(`crm.proposta.estados.${x}`) }))} />
        </Rotulo>
      </form>
    </Drawer>
  );
}
